import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import get from 'lodash/get';

import {
  assignmentTypes,
  assignmentPermissions,
} from '../../shared/authorization';

import {
  currentPeriodSelector,
  companyCodesByAssignmentPermissionSelector,
  taxYearsOptionsSelector,
  subledgerFlagCompanyCodesSelector,
} from '../../shared/store/selectors';

import {
  typeDescriptionsSelector,
  activityDescriptionsSelector,
  taxEntriesTreeSelector,
  countryTaxTypesOptionsSelector,
} from '../store/selectors';

import {
  fetchTaxEntriesTree,
  fetchCountryTaxTypes,
  fetchTypeDescriptions,
  fetchActivityDescriptions,
} from '../store/actions';

import { CountryTaxTypeStaticOptions } from '../../../src/shared/constants';

const assignmentToCheck = {
  assignmentType: assignmentTypes.CC,
  requiredPermission: assignmentPermissions.SubledgerAdd,
};

const getOptions = (tree, dictionary, ...values) => values.every(value => value !== '')
  ? Object.keys(get(tree, values, tree)).map(value => ({ label: dictionary[value] || value, value }))
  : [];

const bookTypeDescriptions = { P: 'GAAP', G: 'G2S' };

const useTaxEntriesTree = (formik, flags = {}) => {
  const { values: {
    companyCode,
    taxYear: fromTaxYear,
    toTaxYear,
    countryTaxType,
    entryType,
    entryActivity,
    bookType,
  } } = formik;

  const taxYear = (toTaxYear !== '' && toTaxYear > fromTaxYear) ? toTaxYear : fromTaxYear;

  const dispatch = useDispatch();

  const taxEntriesTree = useSelector(taxEntriesTreeSelector);

  const currentPeriod = useSelector(currentPeriodSelector);
  const companyCodes = useSelector(flags.forView
    ? subledgerFlagCompanyCodesSelector
    : state => companyCodesByAssignmentPermissionSelector({ ...state, assignmentToCheck })
  );
  const taxYears = useSelector(taxYearsOptionsSelector);
  const countryTaxTypes = useSelector(countryTaxTypesOptionsSelector);
  const typeDescriptions = useSelector(typeDescriptionsSelector);
  const activityDescriptions = useSelector(activityDescriptionsSelector);

  useEffect(() => {
    dispatch(fetchTypeDescriptions(currentPeriod.name));
    dispatch(fetchActivityDescriptions(currentPeriod.name));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (companyCode) {
      dispatch(fetchCountryTaxTypes(companyCode, currentPeriod, CountryTaxTypeStaticOptions.FBOS));
    }
  }, [dispatch, currentPeriod, companyCode]);

  useEffect(() => {
    if (companyCode && taxYear && countryTaxType) {
      dispatch(fetchTaxEntriesTree({
        periodName: currentPeriod.name,
        companyCode,
        taxYear,
        countryTaxType,
        ...flags,
      }));
    }
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    currentPeriod.name,
    companyCode,
    taxYear,
    countryTaxType,
    dispatch,
    flags.forEntry,
    flags.forReclass,
  ]);

  const entryTypes = getOptions(taxEntriesTree, typeDescriptions);
  const entryActivities = getOptions(taxEntriesTree, activityDescriptions, entryType);
  const bookTypes = getOptions(taxEntriesTree, bookTypeDescriptions, entryType, entryActivity);

  const combination = get(taxEntriesTree, [entryType, entryActivity, bookType], {});

  return {
    companyCodes,
    taxYears,
    countryTaxTypes,
    entryTypes,
    entryActivities,
    bookTypes,
    combination,
  };
};

export default useTaxEntriesTree;
